// Web Routes Configuration
// Defines the mapping between URL paths and screen names
// By default, routes follow the pattern /screen-name unless custom routes are defined

import { SCREEN_VALUES } from '../contexts/AppContext';

const SCREEN_VALUES_SET: Set<string> = new Set(SCREEN_VALUES);

export interface WebRoute {
  path: string;
  screen: string;
  isDefault?: boolean; // For the root path
  isCustom?: boolean; // For non-standard routes
}

// Custom route mappings (override default behavior)
const customRoutes: WebRoute[] = [
  { path: '/', screen: 'welcome', isDefault: true, isCustom: true },
  { path: '/welcome', screen: 'welcome', isCustom: true }, // Alternative to root
  { path: '/accept-invitation', screen: 'invitation-acceptance', isCustom: true }, // Invitation acceptance
  { path: '/facebook-callback', screen: 'facebook-callback', isCustom: true }, // Facebook OAuth callback
];

// Generate default routes for all screens (follows /screen-name pattern)
const defaultRoutes: WebRoute[] = [
  { path: '/ai-setup', screen: 'ai-setup' },
  { path: '/ai-usage', screen: 'ai-usage' },
  { path: '/login', screen: 'login' },
  { path: '/unified-login', screen: 'unified-login' },
  { path: '/business-picker', screen: 'business-picker' },
  { path: '/business-creation-wizard', screen: 'business-creation-wizard' },
  { path: '/plan-selection', screen: 'plan-selection' },
  { path: '/billing/return', screen: 'billing-return' },
  { path: '/billing/addons', screen: 'billing-addons' },
  { path: '/channel-onboarding-stepper', screen: 'channel-onboarding-stepper' },
  { path: '/register', screen: 'register' },
  { path: '/otp-verification', screen: 'otp-verification' },
  { path: '/forgot-password', screen: 'forgot-password' },
  { path: '/reset-password', screen: 'reset-password' },
  { path: '/business-setup', screen: 'business-setup' },
  { path: '/subscription', screen: 'subscription' },
  { path: '/home', screen: 'home' },
  { path: '/dashboard', screen: 'dashboard', isCustom: true }, // New admin analytics dashboard (Session 1, not yet wired to 'home') — testing entry point
  { path: '/dashboard/team', screen: 'dashboard-team-all', isCustom: true },
  { path: '/dashboard/templates', screen: 'dashboard-templates-all', isCustom: true },
  { path: '/flows/runs', screen: 'dashboard-flows-all', isCustom: true }, // Flow runs (full layout)
  { path: '/dashboard/flows', screen: 'dashboard-flows-all', isCustom: true }, // old link
  { path: '/dashboard/assignments', screen: 'dashboard-assignments-all', isCustom: true },
  { path: '/social', screen: 'social' },
  { path: '/performance', screen: 'performance' },
  { path: '/whatsapp-test', screen: 'whatsapp-test' },
  { path: '/whatsapp-business-setup', screen: 'whatsapp-business-setup' },
  { path: '/facebook-messenger-setup', screen: 'facebook-messenger-setup' },
  { path: '/contacts', screen: 'contacts' },
  { path: '/notifications', screen: 'notifications' },
  { path: '/chat', screen: 'chat' },
  { path: '/conversation', screen: 'conversation' },
  { path: '/conversation/:id', screen: 'conversation', isCustom: true }, // Conversation with ID
  { path: '/task-history', screen: 'task-history' },
  { path: '/task-history/:taskId', screen: 'task-history', isCustom: true }, // Task history with task ID
  { path: '/settings', screen: 'settings' },
  { path: '/settings/business', screen: 'business-profile' },
  { path: '/departments', screen: 'departments' },
  { path: '/department/add', screen: 'department-add', isCustom: true }, // Add new department
  { path: '/department/edit/:departmentId', screen: 'department-edit', isCustom: true }, // Edit department
  { path: '/products/:catalogueId', screen: 'products-list', isCustom: true }, // Products list with catalogue ID (handles /products/default and /products/{id})
  { path: '/product/:productId', screen: 'product-detail', isCustom: true }, // Product detail
  { path: '/product/create/:catalogueId', screen: 'product-create', isCustom: true }, // Create product
  { path: '/product/edit/:productId', screen: 'product-edit', isCustom: true }, // Edit product
  { path: '/products/import/:catalogueId', screen: 'product-bulk-import', isCustom: true }, // Bulk import
  { path: '/catalogues', screen: 'catalogues' }, // Catalogues list
  { path: '/catalogue/:catalogueId', screen: 'catalogue-detail', isCustom: true }, // Catalogue detail
  { path: '/catalogue/create', screen: 'catalogue-create' }, // Create catalogue
  { path: '/catalogue/edit/:catalogueId', screen: 'catalogue-edit', isCustom: true }, // Edit catalogue
  { path: '/catalogue/:catalogueId/add-products', screen: 'add-products-to-catalogue', isCustom: true }, // Add products to catalogue
  { path: '/catalogue/:catalogueId/channels', screen: 'manage-catalogue-channels', isCustom: true }, // Manage catalogue channels
  { path: '/quick-replies', screen: 'quick-replies' }, // Quick reply sets list
  { path: '/quick-reply-items', screen: 'quick-reply-items' }, // Quick reply items list
  { path: '/quick-reply-set/create', screen: 'quick-reply-set-create' }, // Create quick reply set
  { path: '/quick-reply-set/:setId', screen: 'quick-reply-set-detail', isCustom: true }, // Quick reply set detail
  { path: '/quick-reply-set/edit/:setId', screen: 'quick-reply-set-edit', isCustom: true }, // Edit quick reply set
  { path: '/quick-reply-set/:setId/items', screen: 'quick-reply-set-items', isCustom: true }, // Quick reply set items management
  { path: '/quick-reply-set/:setId/item/create', screen: 'quick-reply-item-create', isCustom: true }, // Create quick reply item (within a set)
  { path: '/quick-reply-item/create', screen: 'quick-reply-item-create' }, // Create quick reply item (standalone)
  { path: '/quick-reply-item/edit/:itemId', screen: 'quick-reply-item-edit', isCustom: true }, // Edit quick reply item
  { path: '/flows', screen: 'flows-list' }, // Flows list
  { path: '/flow/create', screen: 'flow-edit' }, // Create flow
  { path: '/flow/edit/:flowId', screen: 'flow-edit', isCustom: true }, // Edit flow with flow ID
  { path: '/calendar-booking', screen: 'calendar-booking', isCustom: true }, // Calendar booking from WhatsApp
  { path: '/product-view', screen: 'product-view', isCustom: true }, // Public product view from WhatsApp
  { path: '/catalog-view', screen: 'catalog-view', isCustom: true }, // Public catalog view from WhatsApp
  { path: '/calendar', screen: 'business-calendar' }, // Calendar (primary route)
  { path: '/calendar/:calendarId', screen: 'business-calendar', isCustom: true }, // Calendar with selected calendar ID
  { path: '/business-calendar', screen: 'business-calendar', isCustom: true }, // Business calendar view (alternative)
  { path: '/business-calendar/:calendarId', screen: 'business-calendar', isCustom: true }, // Business calendar view with selected calendar
  { path: '/calendars', screen: 'calendars' }, // Calendar list/management
  { path: '/event/create', screen: 'event-create' }, // Create event
  { path: '/event/create/:calendarId', screen: 'event-create', isCustom: true }, // Create event with calendar ID
  { path: '/event/edit/:eventId', screen: 'event-edit', isCustom: true }, // Edit event with event ID
  { path: '/cart', screen: 'cart', isCustom: true }, // Shopping cart
  { path: '/checkout', screen: 'checkout', isCustom: true }, // Checkout
  { path: '/order-success', screen: 'order-success', isCustom: true }, // Order success
  { path: '/track-order', screen: 'track-order', isCustom: true }, // Public order tracking
  { path: '/orders', screen: 'orders', isCustom: true }, // Orders management
  { path: '/order/add', screen: 'order-add', isCustom: true }, // Add new order
  { path: '/order/edit/:orderId', screen: 'order-edit', isCustom: true }, // Edit order
  { path: '/shipping-methods', screen: 'shipping-methods', isCustom: true }, // Shipping methods list
  { path: '/shipping-method/create', screen: 'shipping-method-create', isCustom: true }, // Create shipping method
  { path: '/shipping-method/edit/:methodId', screen: 'shipping-method-edit', isCustom: true }, // Edit shipping method
  { path: '/data-lists', screen: 'data-lists' }, // Data Lists index
  { path: '/lead-finder', screen: 'lead-finder' }, // Lead Finder searches
  { path: '/lead-run/:id', screen: 'lead-run', isCustom: true }, // A Lead Finder search and its results
  { path: '/assignment-rules', screen: 'assignment-rules' }, // Assignment Setup: the rules
  { path: '/assignment-rule/:id', screen: 'assignment-rule-edit', isCustom: true }, // One rule (id or 'new')
  { path: '/data-list/schema/create', screen: 'data-list-schema-create' }, // Create a data list
  { path: '/data-list/schema/:id/edit', screen: 'data-list-schema-edit', isCustom: true }, // Edit a data list's schema
  { path: '/data-list/:id/records', screen: 'data-list-records', isCustom: true }, // A data list's records
  { path: '/data-list/:listId/record/create', screen: 'data-list-record-create', isCustom: true }, // Create a record
  { path: '/data-list/:listId/record/:recordId/edit', screen: 'data-list-record-edit', isCustom: true }, // Edit a record
  { path: '/visits', screen: 'visits' }, // Visits list
  { path: '/visits-setup', screen: 'visits-setup' }, // Visits service config
  { path: '/visit/create', screen: 'visit-create' }, // Create a single visit
  { path: '/visit/:visitId', screen: 'visit-detail', isCustom: true }, // Visit detail — was missing entirely, so a reload on this or visit-execute previously crashed (routeParams is in-memory-only, lost on reload with no URL fallback)
  { path: '/visit/:visitId/execute', screen: 'visit-execute', isCustom: true }, // Visit execution (start/in-progress/end) — the actual screen this fixes reload for
  { path: '/visit-route', screen: 'visit-route' }, // Multi-visit route planning
  { path: '/visits-campaigns', screen: 'visits-campaigns' }, // Visit campaigns list
  { path: '/visit-campaign/create', screen: 'visit-campaign-create' }, // Create a visit campaign
  { path: '/visit-campaign/:campaignId', screen: 'visit-campaign-detail', isCustom: true }, // Visit campaign detail
  { path: '/visit-campaign/:campaignId/distribute', screen: 'visit-campaign-distribute', isCustom: true }, // Visit campaign distribution
];

// Combine custom and default routes
export const webRoutes: WebRoute[] = [
  ...customRoutes,
  ...defaultRoutes,
];

// Helper function to get screen from URL path
export const getScreenFromPath = (path: string): { screen: string | null; params: Record<string, string> } => {
  console.log('🔍 [getScreenFromPath] Called with path:', path);
  
  // Special handling for /products (should map to products-list with default catalogueId)
  if (path === '/products') {
    return { screen: 'products-list', params: { catalogueId: 'default' } };
  }
  
  // Special handling for /catalogues (should map to catalogues screen)
  if (path === '/catalogues') {
    return { screen: 'catalogues', params: {} };
  }
  
  // Special handling for /quick-replies (should map to quick-replies screen)
  if (path === '/quick-replies') {
    return { screen: 'quick-replies', params: {} };
  }
  
  // Special handling for /orders (should map to orders screen)
  if (path === '/orders') {
    return { screen: 'orders', params: {} };
  }
  
  // Special handling for /calendars (should map to calendars screen)
  if (path === '/calendars') {
    return { screen: 'calendars', params: {} };
  }
  
  // First try exact match
  const exactRoute = webRoutes.find(route => route.path === path);
  if (exactRoute) {
    console.log('🔍 [getScreenFromPath] Found exact route:', exactRoute);
    return { screen: exactRoute.screen, params: {} };
  }
  
  // Then try dynamic routes (with parameters)
  for (const route of webRoutes) {
    if (route.path.includes(':')) {
      console.log('🔍 [getScreenFromPath] Checking dynamic route:', route);
      const routePattern = route.path.replace(/:([^/]+)/g, '([^/]+)');
      console.log('🔍 [getScreenFromPath] Route pattern:', routePattern);
      const regex = new RegExp(`^${routePattern}$`);
      const match = path.match(regex);
      console.log('🔍 [getScreenFromPath] Match result:', match);
      
      if (match) {
        const params: Record<string, string> = {};
        const paramNames = route.path.match(/:([^/]+)/g);
        if (paramNames) {
          paramNames.forEach((paramName, index) => {
            const key = paramName.substring(1); // Remove the ':'
            params[key] = match[index + 1]; // match[0] is the full match
          });
        }
        console.log('🔍 [getScreenFromPath] Found dynamic route with params:', { screen: route.screen, params });
        return { screen: route.screen, params };
      }
    }
  }
  
  // Final fallback: any screen missing its own defaultRoutes entry still
  // round-trips correctly, since getPathFromScreen's own fallback already
  // produces `/${screen}` for exactly this case (:211/:220 below) — this is
  // the reverse direction of that same fallback. Strips the leading slash
  // and checks it against every real Screen value (SCREEN_VALUES, imported
  // from AppContext.tsx — the single source of truth for screen names) so a
  // refresh/deep-link on a screen that was simply never added to
  // defaultRoutes no longer falls through to null (and from there to the
  // 'welcome' screen) the way it did before. Screens with real dynamic
  // params (e.g. :id) still need an explicit defaultRoutes entry above —
  // this only recovers the bare `/screen-name` case.
  const stripped = path.startsWith('/') ? path.slice(1) : path;
  if (stripped && SCREEN_VALUES_SET.has(stripped)) {
    return { screen: stripped, params: {} };
  }

  return { screen: null, params: {} };
};

// Helper function to get URL path from screen
export const getPathFromScreen = (screen: string, params?: Record<string, string>): string => {
  console.log('🔍 [getPathFromScreen] Called with:', { screen, params });
  
  // Validate inputs
  if (typeof screen !== 'string') {
    console.error('🔍 [getPathFromScreen] Screen is not a string:', screen);
    return '/';
  }
  
  if (params && typeof params !== 'object') {
    console.error('🔍 [getPathFromScreen] Params is not an object:', params);
    params = undefined;
  }
  
  if (params) {
    // Look for dynamic routes for this screen
    const dynamicRoute = webRoutes.find(route => 
      route.screen === screen && route.path.includes(':')
    );
    
    console.log('🔍 [getPathFromScreen] Dynamic route found:', dynamicRoute);
    
    if (dynamicRoute) {
      let path = dynamicRoute.path;
      console.log('🔍 [getPathFromScreen] Initial path:', path, 'type:', typeof path);
      
      // Replace parameters in the path - ensure values are strings
      Object.keys(params).forEach(key => {
        const value = String(params[key]); // Ensure it's a string
        console.log('🔍 [getPathFromScreen] Replacing:', `:${key}`, 'with:', value, 'type:', typeof value);
        path = path.replace(`:${key}`, value);
      });
      
      console.log('🔍 [getPathFromScreen] Final path:', path, 'type:', typeof path);
      
      // Safety check - ensure we return a string
      if (typeof path !== 'string') {
        console.error('🔍 [getPathFromScreen] Path is not a string, returning fallback');
        return `/${screen}`;
      }
      
      return path;
    }
  }
  
  // Fallback to exact route match
  const route = webRoutes.find(route => route.screen === screen && !route.path.includes(':'));
  const fallbackPath = route ? route.path : `/${screen}`;
  console.log('🔍 [getPathFromScreen] Fallback path:', fallbackPath, 'type:', typeof fallbackPath);
  
  // Safety check - ensure we return a string
  if (typeof fallbackPath !== 'string') {
    console.error('🔍 [getPathFromScreen] Fallback path is not a string, returning default');
    return `/${screen}`;
  }
  
  return fallbackPath;
};

// Helper function to get default route
export const getDefaultRoute = (): WebRoute => {
  return webRoutes.find(route => route.isDefault) || webRoutes[0];
};

// Helper function to check if a path is valid
export const isValidPath = (path: string): boolean => {
  return webRoutes.some(route => route.path === path);
};

// Helper function to get all available routes
export const getAllRoutes = (): WebRoute[] => {
  return [...webRoutes];
};

// Helper function to add a custom route dynamically
export const addCustomRoute = (path: string, screen: string): void => {
  const existingIndex = webRoutes.findIndex(route => route.path === path);
  if (existingIndex !== -1) {
    // Update existing route
    webRoutes[existingIndex] = { path, screen, isCustom: true };
  } else {
    // Add new custom route
    webRoutes.push({ path, screen, isCustom: true });
  }
};

// Helper function to remove a custom route
export const removeCustomRoute = (path: string): boolean => {
  const index = webRoutes.findIndex(route => route.path === path && route.isCustom);
  if (index !== -1) {
    webRoutes.splice(index, 1);
    return true;
  }
  return false;
};

// Helper function to get all custom routes
export const getCustomRoutes = (): WebRoute[] => {
  return webRoutes.filter(route => route.isCustom);
};

// Helper function to get all default routes
export const getDefaultRoutes = (): WebRoute[] => {
  return webRoutes.filter(route => !route.isCustom);
};
